import { Flex } from '@chakra-ui/react'
import { SearchX } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouteError } from 'react-router'
import styled from 'styled-components'
import { FontStyle, Text } from '~/components/core'
import { MangoButton } from '~/components/shared/MangoButton'
import { useCurrentRootPath } from '~/hooks/useRouting'

const NotFoundNoticeWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem;
  user-select: none;
  background-color: var(--chakra-colors-bg-subtle);
`

const ImageWrapper = styled.div`
  margin-bottom: 26px;
`

const NotFoundContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const NotFoundTextWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 62px;
  width: 375px;
`
const PANEL_ANIMATION_TIME_SECONDS = 0.3
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: auto;
  margin-top: 16px;
`
export interface NotFoundErrorOptions {
  cause: {
    status: number
    statusText: string
  }
}
export const throwNotFoundError = (
  message: string,
  options: NotFoundErrorOptions,
) => {
  throw new Error(message, options)
}

export const ResourceErrorBoundary = () => {
  const { t } = useTranslation()
  const currentRoot = useCurrentRootPath()
  const navigate = useNavigate()
  const error = useRouteError() as Error & { cause?: { status: number } }
  let errorMessage = t(
    'resourceErrorBoundary.defaultMessage',
    'An unexpected error occurred.',
  )
  if (error && typeof error === 'object') {
    if (error.message) {
      errorMessage = error.message
    }
  }
  const message =
    errorMessage ||
    t(
      'resourceErrorBoundary.notFoundFallback',
      'Oops this record has either been deleted or does not exist. If you need help, please contact support.',
    )
  return (
    <Flex
      data-testid="resource-error-boundary"
      position={'inherit'}
      right="0rem"
      height="full"
      transition={`width ${PANEL_ANIMATION_TIME_SECONDS}s`}
      borderLeft="2px solid"
      borderLeftColor="bg.emphasized"
    >
      <NotFoundNoticeWrapper>
        <NotFoundContentWrapper>
          <ImageWrapper>
            <SearchX size={56} />
          </ImageWrapper>
          <NotFoundTextWrapper>
            <Text
              color="var(--chakra-colors-fg-default)"
              size={FontStyle.Normal}
              align="center"
            >
              {message}
            </Text>
          </NotFoundTextWrapper>
          <ButtonWrapper>
            <MangoButton
              onClick={() =>
                navigate(currentRoot, {
                  replace: true,
                })
              }
            >
              {t('resourceErrorBoundary.takeMeBack', 'Take me back')}
            </MangoButton>
          </ButtonWrapper>
        </NotFoundContentWrapper>
      </NotFoundNoticeWrapper>
    </Flex>
  )
}
